import React, { useEffect, useMemo, useRef, useState } from "react";
import initialData from "../data/professionData.json";
import { Link } from "react-router-dom";

// -----------------------------------------------------
// Config (lokal)
// -----------------------------------------------------
const STORAGE_KEY = "professionDataDraft";
const IMG_BASE = "https://www.denisbitter.de/images/";

// -----------------------------------------------------
// Helpers
// -----------------------------------------------------
const cx = (...xs) => xs.filter(Boolean).join(" ");

const toImgUrl = (value) => {
  if (!value) return "";
  if (String(value).startsWith("http")) return value;
  const file = String(value).replace(/^\/?images\/?/, "").replace(/^\/+/, "");
  return IMG_BASE + file;
};

const newBlankItem = () => ({
  title: "Neuer Eintrag",
  problem: "Worin liegt das Problem?",
  challenge: "Was ist die Hürde?",
  solution: "Kurze, klare Lösung in deiner Sprache.",
  link: "",
  imageId: "",
  videoId: "",
});

function download(filename, text) {
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ⬇️ FIXED: useDraft ignoriert leere/ungültige Drafts und fällt auf initialData zurück
function useDraft(initial) {
  const normalizedInitial = Array.isArray(initial)
    ? initial.map((it) => ({ ...it, imageId: toImgUrl(it.imageId) }))
    : [];

  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch {}
    return normalizedInitial;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items, null, 2));
    } catch {}
  }, [items]);

  return [items, setItems];
}

function validateItem(item) {
  const errors = [];
  if (!item.title?.trim()) errors.push("Titel fehlt");
  if (!item.problem?.trim()) errors.push("Problem fehlt");
  if (!item.challenge?.trim()) errors.push("Challenge fehlt");
  if (!item.solution?.trim()) errors.push("Lösung fehlt");
  if (item.link && !(item.link.startsWith("/") || item.link.startsWith("http"))) {
    errors.push("Link sollte mit '/' oder 'http' beginnen");
  }
  return errors;
}

// -----------------------------------------------------
// UI Elemente
// -----------------------------------------------------
const Pill = ({ children, tone = "neutral" }) => (
  <span
    className={cx(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] border",
      tone === "ok" && "bg-green-50 border-green-300 text-green-900",
      tone === "warn" && "bg-yellow-50 border-yellow-300 text-yellow-900",
      tone === "bad" && "bg-red-50 border-red-300 text-red-900",
      tone === "neutral" && "bg-gray-50 border-gray-300 text-gray-700"
    )}
  >
    {children}
  </span>
);

function Field({ label, children, hint }) {
  return (
    <label className="block mb-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-600 mb-1">
        {label}
      </div>
      {children}
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={cx(
        "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm",
        "focus:outline-none focus:ring-2 focus:ring-gray-900/10"
      )}
    />
  );
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      className={cx(
        "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm min-h-[64px]",
        "focus:outline-none focus:ring-2 focus:ring-gray-900/10"
      )}
    />
  );
}

function ToolbarButton({ children, onClick, tone = "default", title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cx(
        "px-3 h-8 rounded-md text-xs border shadow-sm",
        tone === "default" && "bg-white border-gray-300 hover:bg-gray-50",
        tone === "primary" &&
          "bg-gray-900 text-white border-gray-900 hover:bg-black",
        tone === "danger" &&
          "bg-red-600 text-white border-red-600 hover:bg-red-700"
      )}
    >
      {children}
    </button>
  );
}

function PreviewCard({ item, index }) {
  const hasVideo = !!item.videoId;
  const hasImage = !!item.imageId;
  return (
    <div className="relative bg-gradient-to-br from-gray-50 via-orange-50/25 to-gray-50 text-gray-900 rounded-xl min-h-[240px] shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-[11px] text-orange-600">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-sm font-semibold">
            {item.title || "(ohne Titel)"}
          </span>
          {hasVideo && <Pill tone="ok">Video</Pill>}
          {!hasVideo && hasImage && <Pill tone="neutral">Bild</Pill>}
          {!hasVideo && !hasImage && <Pill tone="warn">kein Media</Pill>}
        </div>
        <div className="text-xs text-gray-900 mb-1">{item.problem}</div>
        <div className="text-xs text-gray-700">{item.challenge}</div>
        <div className="mt-3 text-xs italic text-gray-600 line-clamp-3">
          {item.solution}
        </div>
        {item.link && (
          <div className="mt-3">
            <Link to={item.link} className="text-xs underline text-gray-900">
              {item.link}
            </Link>
          </div>
        )}
      </div>
      {(item.videoId || item.imageId) && (
        <div className="h-[120px] w-full border-t border-gray-200 bg-white/30 flex items-center justify-center">
          {item.videoId ? (
            <div className="text-[10px]">YouTube Preview: {item.videoId}</div>
          ) : (
            <img
              src={toImgUrl(item.imageId)}
              alt="Preview"
              className="h-full w-full object-cover"
            />
          )}
        </div>
      )}
    </div>
  );
}

function EditorForm({ item, onChange }) {
  return (
    <div>
      <Field label="Titel">
        <TextInput
          value={item.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </Field>
      <Field label="Problem">
        <TextArea
          value={item.problem}
          onChange={(e) => onChange({ problem: e.target.value })}
        />
      </Field>
      <Field label="Challenge">
        <TextArea
          value={item.challenge}
          onChange={(e) => onChange({ challenge: e.target.value })}
        />
      </Field>
      <Field label="Lösung">
        <TextArea
          value={item.solution}
          onChange={(e) => onChange({ solution: e.target.value })}
        />
      </Field>
      <Field label="Link">
        <TextInput
          value={item.link}
          onChange={(e) => onChange({ link: e.target.value })}
        />
      </Field>
      <Field label="Bild (imageId)">
        <TextInput
          placeholder="z.B. jsx_psd_2.svg oder /images/jsx_psd_2.svg"
          value={item.imageId}
          onChange={(e) => onChange({ imageId: toImgUrl(e.target.value) })}
        />
        <div className="text-[10px] text-gray-500 mt-1 break-all">
          Wird genutzt als: {toImgUrl(item.imageId)}
        </div>
      </Field>
      <Field label="Video (videoId)">
        <TextInput
          value={item.videoId}
          onChange={(e) => onChange({ videoId: e.target.value })}
        />
      </Field>
    </div>
  );
}

// -----------------------------------------------------
// Main Component
// -----------------------------------------------------
export default function ProfessionEditor() {
  const [items, setItems] = useDraft(initialData);
  const [selected, setSelected] = useState(0);
  const fileRef = useRef(null);

  const validations = useMemo(() => items.map(validateItem), [items]);
  const totalErrors = validations.reduce((sum, errs) => sum + errs.length, 0);
  const selectedErrors = validations[selected] || [];

  function update(idx, patch) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function move(idx, dir) {
    setItems((prev) => {
      const next = [...prev];
      const ni = idx + dir;
      if (ni < 0 || ni >= next.length) return prev;
      const [x] = next.splice(idx, 1);
      next.splice(ni, 0, x);
      return next;
    });
    setSelected((s) => Math.max(0, Math.min(items.length - 1, s + dir)));
  }

  function remove(idx) {
    if (!window.confirm("Diesen Eintrag löschen?")) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setSelected((s) => (s > 0 ? s - 1 : 0));
  }

  function duplicate(idx) {
    setItems((prev) => {
      const next = [...prev];
      next.splice(idx + 1, 0, {
        ...prev[idx],
        title: (prev[idx].title || "Eintrag") + " (Kopie)",
      });
      return next;
    });
    setSelected(idx + 1);
  }

  function addNew() {
    setItems((prev) => [...prev, newBlankItem()]);
    setSelected(items.length);
  }

  function resetDraft() {
    if (!window.confirm("Entwurf wirklich zurücksetzen?")) return;
    localStorage.removeItem(STORAGE_KEY);
    setItems(
      (Array.isArray(initialData) ? initialData : []).map((it) => ({
        ...it,
        imageId: toImgUrl(it.imageId),
      }))
    );
    setSelected(0);
  }

  function onImportFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(String(reader.result));
        if (!Array.isArray(json)) throw new Error("JSON muss ein Array sein");
        const normalized = json.map((it) => ({ ...it, imageId: toImgUrl(it.imageId) }));
        setItems(normalized);
        setSelected(0);
      } catch (err) {
        alert("Fehler beim Import: " + err.message);
      }
    };
    reader.readAsText(file);
  }

  const jsonText = useMemo(() => JSON.stringify(items, null, 2), [items]);

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white via-orange-50/40 to-white">
      <div className="max-w-screen-2xl mx-auto p-6 lg:p-10">
        <header className="flex flex-col md:flex-row md:items-center gap-4 justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Profession Data — Smart Editor (Lokal)
            </h1>
            <p className="text-xs text-gray-600">
              {items.length} Einträge · {totalErrors} Hinweise
            </p>
            {items.length === 0 && (
              <div className="mt-3 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                Keine Einträge gefunden. Möglicherweise liegt ein leerer Entwurf in deinem Browser-Cache.
                <button className="ml-2 underline" onClick={resetDraft}>
                  Beispieldaten laden
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <ToolbarButton onClick={addNew}>+ Neu</ToolbarButton>
            <ToolbarButton onClick={() => duplicate(selected)}>Duplizieren</ToolbarButton>
            <ToolbarButton onClick={() => move(selected, -1)}>▲</ToolbarButton>
            <ToolbarButton onClick={() => move(selected, +1)}>▼</ToolbarButton>
            <ToolbarButton tone="danger" onClick={() => remove(selected)}>Löschen</ToolbarButton>

            <ToolbarButton onClick={() => fileRef.current?.click()}>Import</ToolbarButton>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={onImportFile}
            />

            <ToolbarButton onClick={() => download("professionData.json", jsonText)} tone="primary">
              Export
            </ToolbarButton>
            <ToolbarButton onClick={() => navigator.clipboard?.writeText(jsonText)}>
              Kopieren
            </ToolbarButton>

            <ToolbarButton
              onClick={() => setItems((prev) => prev.map((it) => ({ ...it, imageId: toImgUrl(it.imageId) })))}
              title="Alle imageId → absolute URL"
            >
              Bildpfade fixen
            </ToolbarButton>

            <ToolbarButton onClick={resetDraft}>Reset</ToolbarButton>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Liste */}
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="rounded-xl border bg-white">
              <div className="p-3 border-b text-[11px] uppercase tracking-wide text-gray-600">
                Einträge
              </div>
              <ul className="max-h-[60vh] overflow-auto divide-y">
                {items.map((it, i) => {
                  const errs = validations[i];
                  const isSel = i === selected;
                  return (
                    <li key={i}>
                      <button
                        onClick={() => setSelected(i)}
                        className={cx(
                          "w-full text-left p-3 text-sm flex items-start gap-2",
                          isSel ? "bg-orange-50" : "hover:bg-gray-50"
                        )}
                      >
                        <span className="font-mono text-[11px] text-gray-500 mt-0.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="flex-1">
                          <span className="font-medium block truncate">
                            {it.title || "(ohne Titel)"}
                          </span>
                          <span className="text-[11px] text-gray-500 line-clamp-1">
                            {it.problem}
                          </span>
                        </span>
                        {errs.length > 0 ? (
                          <Pill tone="warn">{errs.length}</Pill>
                        ) : (
                          <Pill tone="ok">OK</Pill>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Formular + Preview */}
          <main className="lg:col-span-8 xl:col-span-9 grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            <section className="rounded-xl border bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Eintrag bearbeiten</h2>
                {selectedErrors.length > 0 ? (
                  <Pill tone="warn">{selectedErrors.length} Hinweise</Pill>
                ) : (
                  <Pill tone="ok">Valide</Pill>
                )}
              </div>

              {items[selected] ? (
                <EditorForm
                  item={items[selected]}
                  onChange={(patch) => update(selected, patch)}
                />
              ) : (
                <div className="text-sm text-gray-600">Kein Eintrag ausgewählt.</div>
              )}

              {selectedErrors.length > 0 && (
                <ul className="mt-4 text-[12px] text-red-700 list-disc ml-5">
                  {selectedErrors.map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-xl border bg-white p-4">
              <h2 className="text-sm font-semibold mb-3">Live-Preview</h2>
              {items[selected] ? (
                <PreviewCard item={items[selected]} index={selected} />
              ) : (
                <div className="text-sm text-gray-600">Kein Eintrag ausgewählt.</div>
              )}
              <div className="mt-4">
                <details>
                  <summary className="cursor-pointer text-xs text-gray-700">
                    Aktueller JSON-Eintrag
                  </summary>
                  <pre className="mt-2 text-[11px] bg-gray-50 p-3 rounded-md overflow-auto max-h-[200px]">
                    {JSON.stringify(items[selected] ?? {}, null, 2)}
                  </pre>
                </details>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
